// Globals
import { useTranslation } from 'react-i18next';

// Models
import { FieldTypes, ISettingsMap } from '@/models/settings.model';

// Components
import {
  StyledBlock,
  StyledContainer,
  StyledContentBlock,
  StyledLabel,
  StyledList,
  StyledSection,
  StyledTag,
  StyledText,
  StyledTitle,
} from '../Styled';

type TSettingsDetailsProps = {
  settings: Partial<ISettingsMap>;
};

const SettingsDetails = ({ settings }: TSettingsDetailsProps) => {
  const { t } = useTranslation(['settings']);
  return (
    <StyledContentBlock>
      <StyledSection>
        <StyledLabel>{t('admin_emails')}</StyledLabel>
        <StyledList>
          {settings?.[FieldTypes.ADMIN_EMAILS]?.value.map((item) => (
            <StyledTag key={item}>{item}</StyledTag>
          ))}
        </StyledList>
      </StyledSection>
      <StyledSection>
        <StyledLabel>{t('order_emails')}</StyledLabel>
        <StyledList>
          {settings?.[FieldTypes.ORDER_EMAILS]?.value.map((item) => (
            <StyledTag key={item}>{item}</StyledTag>
          ))}
        </StyledList>
      </StyledSection>
      <StyledSection>
        <StyledLabel>{t('price_change_admin_emails')}</StyledLabel>
        <StyledList>
          {settings?.[FieldTypes.PRICE_CHANGE_ADMIN_EMAILS]?.value?.map((item) => (
            <StyledTag key={item}>{item}</StyledTag>
          ))}
        </StyledList>
      </StyledSection>
      <StyledSection>
        <StyledLabel>{t('google_analytics')}</StyledLabel>
        <StyledText>{settings?.[FieldTypes.GOOGLE_ANALYTICS]?.value || '-'}</StyledText>
      </StyledSection>
      <StyledSection>
        <StyledLabel>{t('min_order_total')}</StyledLabel>
        <StyledText>{String(settings?.[FieldTypes.MIN_ORDER_TOTAL]?.value ?? '-')}</StyledText>
      </StyledSection>
      <StyledContainer>
        <StyledBlock>
          <StyledLabel>{t('support_service')}</StyledLabel>
          <div>
            <StyledLabel $weight="BOLD">{t('contact_phone')}</StyledLabel>
            <StyledText>{settings?.[FieldTypes.CONTACT_PHONE]?.value}</StyledText>
          </div>
          <div>
            <StyledLabel $weight="BOLD">{t('contact_email')}</StyledLabel>
            <StyledText>{settings?.[FieldTypes.CONTACT_EMAIL]?.value}</StyledText>
          </div>
          <div>
            <StyledLabel $weight="BOLD">{t('working_hours')}</StyledLabel>
            <StyledText
              dangerouslySetInnerHTML={{
                __html:
                  settings?.[FieldTypes.WORKING_HOURS]?.locale?.uk?.name ||
                  settings[FieldTypes.WORKING_HOURS]?.locale?.ru?.name ||
                  '',
              }}
            />
          </div>
          <div>
            <StyledLabel $weight="BOLD">{t('address')}</StyledLabel>
            <StyledText>
              {settings?.[FieldTypes.ADDRESS]?.locale?.uk?.name || settings[FieldTypes.ADDRESS]?.locale?.ru?.name}
            </StyledText>
          </div>
          <StyledTitle>{t('merchant_title')}</StyledTitle>
          <div>
            <StyledLabel $weight="BOLD">{t('fop_merchant_id')}</StyledLabel>
            <StyledText>{settings?.[FieldTypes.FOP_MERCHANT]?.value}</StyledText>
          </div>
          <div>
            <StyledLabel $weight="BOLD">{t('fop_merchant_title')}</StyledLabel>
            <StyledText>{settings?.[FieldTypes.FOP_MERCHANT_DESCRIPTION]?.value ?? '-'}</StyledText>
          </div>
          <div>
            <StyledLabel $weight="BOLD">{t('tov_merchant_id')}</StyledLabel>
            <StyledText>{settings?.[FieldTypes.TOV_MERCHANT]?.value}</StyledText>
          </div>
          <div>
            <StyledLabel $weight="BOLD">{t('tov_merchant_title')}</StyledLabel>
            <StyledText>{settings?.[FieldTypes.TOV_MERCHANT_DESCRIPTION]?.value ?? '-'}</StyledText>
          </div>
          <StyledTitle>{t('fiscal_title')}</StyledTitle>
          <div>
            <StyledLabel $weight="BOLD">{t('fop_cash_register_id')}</StyledLabel>
            <StyledText>{settings?.[FieldTypes.FOP_CASH_REGISTER_ID]?.value ?? '-'}</StyledText>
          </div>
          <div>
            <StyledLabel $weight="BOLD">{t('fop_cashier_name')}</StyledLabel>
            <StyledText>{settings?.[FieldTypes.FOP_CASHIER_NAME]?.value ?? '-'}</StyledText>
          </div>
          <div>
            <StyledLabel $weight="BOLD">{t('fop_fiscal_department')}</StyledLabel>
            <StyledText>{settings?.[FieldTypes.FOP_FISCAL_DEPARTMENT]?.value ?? '-'}</StyledText>
          </div>
          <div>
            <StyledLabel $weight="BOLD">{t('tov_cash_register_id')}</StyledLabel>
            <StyledText>{settings?.[FieldTypes.TOV_CASH_REGISTER_ID]?.value ?? '-'}</StyledText>
          </div>
          <div>
            <StyledLabel $weight="BOLD">{t('tov_cashier_name')}</StyledLabel>
            <StyledText>{settings?.[FieldTypes.TOV_CASHIER_NAME]?.value ?? '-'}</StyledText>
          </div>
          <div>
            <StyledLabel $weight="BOLD">{t('tov_fiscal_department')}</StyledLabel>
            <StyledText>{settings?.[FieldTypes.TOV_FISCAL_DEPARTMENT]?.value ?? '-'}</StyledText>
          </div>
        </StyledBlock>
        <StyledBlock>
          <StyledLabel>{t('social_media')}</StyledLabel>
          <StyledBlock>
            {Object.entries(settings?.[FieldTypes.SOCIAL_LINKS]?.value ?? {}).map(([key, value]) => (
              <div key={key}>
                <StyledLabel $weight="BOLD" $capitalize>
                  {t(`social_networks_${key}`, { ns: 'common' })}
                </StyledLabel>
                <StyledText as="a" href={value}>
                  {value}
                </StyledText>
              </div>
            ))}
          </StyledBlock>
        </StyledBlock>
      </StyledContainer>
    </StyledContentBlock>
  );
};

export default SettingsDetails;
